import React, {ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {cssTheme} from '../../../../themes'
import {Text} from '../../Text'

interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string
  /** The label to be shown next to the badge */
  label?: ReactNode
  /** The sub label shown below the label */
  subLabel?: ReactNode
  /** The number to be shown in the badge */
  value: number
  /** The ID of the Score Rating value */
  scoreId?: string
  /** The ID of the Label text i.e. Superb/Good */
  scoreLabelId?: string
  /** The ID of the SubLabel which is review count  */
  scoreSubLabelId?: string
}

const BADGE_SIZE = 44
const BADGE_RADIUS = 30
const BADGE_RADIUS_TAIL = 6

const RatingElement = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
`

const BadgeWrapper = styled.div`
  ${cssTheme.typography.titleL}
  min-width: ${BADGE_SIZE}px;
  line-height: ${BADGE_SIZE}px;
  text-align: center;
  border-radius: ${BADGE_RADIUS}px ${BADGE_RADIUS}px ${BADGE_RADIUS_TAIL}px
    ${BADGE_RADIUS}px;

  .--poor & {
    background-color: ${cssTheme.colors.scoreRating.default.neutral.background};
    color: ${cssTheme.colors.scoreRating.default.neutral.score};
  }
  .--high & {
    background-color: ${cssTheme.colors.scoreRating.default.success.background};
    color: ${cssTheme.colors.scoreRating.default.success.score};
  }
`

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s000};
`

const LabelText = styled(Text)`
  display: flex;
  align-items: center;
  color: ${cssTheme.colors.scoreRating.default.description};
`

const SubLabelText = styled(Text)`
  display: flex;
  align-items: center;
  color: ${cssTheme.colors.scoreRating.default.review};
`

export const ScoreRatingLarge = ({
  label,
  subLabel,
  value,
  className,
  scoreId,
  scoreLabelId,
  scoreSubLabelId
}: Props) => (
  <RatingElement
    className={cx(value >= 7 && '--high', value < 7 && '--poor', className)}
  >
    <BadgeWrapper id={scoreId}>{value || '-'}</BadgeWrapper>
    {(label || subLabel) && (
      <LabelWrapper>
        {label && (
          <LabelText id={scoreLabelId} variant="titleL" tag="span">
            {label}
          </LabelText>
        )}
        {subLabel && (
          <SubLabelText variant="bodyXS" id={scoreSubLabelId}>
            {subLabel}
          </SubLabelText>
        )}
      </LabelWrapper>
    )}
  </RatingElement>
)
